import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Box,
  Chip,
  Autocomplete,
  Stack,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import emailService from '../../services/emailService';
import graphService from '../../services/graphService';
import userService from '../../services/userService';
import BaseDialog from './BaseDialog';
import BaseTextField from './BaseTextField';
import BaseButton from './BaseButton';

const EmailDialog = ({ 
  open, 
  onClose, 
  meeting, 
  summary,
  summaryStats,
  transcript,
  contentType = "summary", // "summary" ou "transcript"
  title
}) => {
  const { currentUser } = useAuth();
  const { addSystemNotification } = useNotifications();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // États pour les destinataires
  const [recipients, setRecipients] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ success: false, error: false, message: '' });

  // Titre par défaut selon le type de contenu
  const defaultTitle = contentType === "transcript" ? "Envoyer la transcription par email" : "Envoyer le résumé par email";
  const dialogTitle = title || defaultTitle;

  // Initialiser avec l'utilisateur connecté et réinitialiser les données
  useEffect(() => {
    if (open) {

      console.log('🔍 Object.keys(currentUser):', currentUser ? Object.keys(currentUser) : 'null');

      // Réinitialiser le status et les données
      setStatus({ success: false, error: false, message: '' });
      setInputValue('');
      setUserOptions([]);
      setCustomMessage('');
      
      // Essayer différentes façons d'accéder à l'email
      let userEmail = null;
      let userName = 'Vous';
      
             if (currentUser) {
         // Tenter différentes propriétés pour l'email
         userEmail = currentUser.email || 
                    currentUser.reloadUserInfo?.email || 
                    currentUser.providerData?.[0]?.email ||
                    currentUser.profile?.email ||
                    currentUser.profile?.mail ||
                    currentUser.profile?.userPrincipalName ||
                    null;
                    
         // Tenter différentes propriétés pour le nom
         userName = currentUser.displayName || 
                   currentUser.reloadUserInfo?.displayName ||
                   currentUser.providerData?.[0]?.displayName ||
                   currentUser.profile?.displayName ||
                   currentUser.profile?.name ||
                   'Vous';

         // Si toujours pas d'email et que c'est un utilisateur Azure, essayer localStorage
         if (!userEmail && currentUser.isAzureUser) {

           try {
             const azureUserJson = localStorage.getItem('azureTestUser');
             if (azureUserJson) {
               const azureUser = JSON.parse(azureUserJson);
               userEmail = azureUser.email || azureUser.mail || azureUser.userPrincipalName;
               userName = azureUser.displayName || azureUser.name || userName;

             }
           } catch (e) {
             addSystemNotification('⚠️ Erreur localStorage Azure:', 'error');
           }
         }
       }
      
      // Initialiser avec l'utilisateur connecté
      if (userEmail) {

        setRecipients([{
          id: 'current-user',
          email: userEmail,
          displayName: userName === userEmail ? 'Vous' : userName,
          title: currentUser?.title || '',
          isCurrentUser: true
        }]);
      } else {

        // Tentative de récupération de l'utilisateur depuis le localStorage ou autre source
        try {
          const azureUserJson = localStorage.getItem('azureTestUser');
          if (azureUserJson) {
            const azureUser = JSON.parse(azureUserJson);
            if (azureUser.email) {

              setRecipients([{
                id: 'current-user',
                email: azureUser.email,
                displayName: azureUser.displayName || 'Vous',
                title: azureUser.title || '',
                isCurrentUser: true
              }]);
              return;
            }
          }
        } catch (e) {
          addSystemNotification('⚠️ Erreur lors de la récupération depuis localStorage:', 'error');
        }
        
        // Si aucun utilisateur trouvé, initialiser avec une liste vide

        setRecipients([]);
      }
    }
  }, [open, currentUser]);

  // Effect séparé pour réagir aux changements de currentUser quand le dialog est ouvert
  useEffect(() => {
    // Si le dialog est ouvert et qu'on n'a pas encore de destinataires, 
    // mais que currentUser devient disponible
    if (open && currentUser && recipients.length === 0) {
      const userEmail = currentUser.email || 
                       currentUser.reloadUserInfo?.email || 
                       currentUser.providerData?.[0]?.email ||
                       null;
                       
      if (userEmail) {

        const userName = currentUser.displayName || 
                        currentUser.reloadUserInfo?.displayName ||
                        currentUser.providerData?.[0]?.displayName ||
                        'Vous';
        
        setRecipients([{
          id: 'current-user',
          email: userEmail,
          displayName: userName === userEmail ? 'Vous' : userName,
          title: currentUser?.title || '',
          isCurrentUser: true
        }]);
      }
    }
  }, [currentUser, open, recipients.length]);

  // Recherche d'utilisateurs pour l'autocomplete (Graph API + Firestore)
  useEffect(() => {
    if (inputValue.length < 2) {
      setUserOptions([]);
      return;
    }
    
    setLoadingUsers(true);
    
    // Rechercher en parallèle dans Graph API et Firestore
    Promise.all([
      graphService.searchUsers(inputValue, 8),
      userService.searchUsers(inputValue)
    ]).then(([graphUsers, firestoreUsers]) => {
      // Combiner et dédupliquer les résultats
      const allUsers = [...graphUsers, ...firestoreUsers];
      const uniqueUsers = allUsers.filter((user, index, arr) => 
        arr.findIndex(u => u.email === user.email) === index
      );
      
      // Filtrer les utilisateurs déjà ajoutés
      const filteredUsers = uniqueUsers.filter(user => 
        !recipients.some(recipient => recipient.email === user.email)
      );

      // Si la saisie ressemble à un email et n'est pas dans les résultats, l'ajouter comme option externe
      const inputLowerCase = inputValue.toLowerCase().trim();
      if (isValidEmail(inputLowerCase)) {
        const emailExists = filteredUsers.some(user => user.email.toLowerCase() === inputLowerCase) ||
                           recipients.some(recipient => recipient.email.toLowerCase() === inputLowerCase);
        
        if (!emailExists) {
          filteredUsers.unshift({
            id: `external-option-${inputLowerCase}`,
            email: inputLowerCase,
            displayName: inputLowerCase,
            title: 'Adresse externe',
            isExternalOption: true
          });
        }
      }
      
      setUserOptions(filteredUsers);
      setLoadingUsers(false);
    }).catch(() => {
      setLoadingUsers(false);
      setUserOptions([]);
    });
  }, [inputValue, recipients]);

  // Validation d'email
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Ajouter un destinataire
  const addRecipient = (email) => {
    if (!email || !isValidEmail(email)) return;
    
    // Vérifier si l'email n'est pas déjà présent
    if (recipients.some(r => r.email.toLowerCase() === email.toLowerCase())) return;
    
    const newRecipient = {
      id: `external-${Date.now()}`,
      email: email,
      displayName: email,
      isExternal: true
    };
    
    setRecipients([...recipients, newRecipient]);
    setInputValue('');
  };

  // Supprimer un destinataire
  const removeRecipient = (recipientToRemove) => {
    setRecipients(recipients.filter(r => r.id !== recipientToRemove.id));
  };

  // Gérer l'entrée dans l'Autocomplete
  const handleAutocompleteChange = (event, value, reason) => {

    if (reason === 'selectOption' && value) {
      const newRecipient = {
        id: value.id || `user-${Date.now()}`,
        email: value.email,
        displayName: value.isExternalOption ? value.email : (value.displayName || value.email),
        title: value.title,
        photoURL: value.photoURL,
        isUser: !value.isExternalOption,
        isExternal: value.isExternalOption
      };
      
      if (!recipients.some(r => r.email === newRecipient.email)) {
        setRecipients([...recipients, newRecipient]);
      }
      
      // Forcer le vidage du champ avec un délai pour laisser l'Autocomplete se mettre à jour
      setTimeout(() => {
        setInputValue('');
      }, 0);
    }
  };

  // Supprimer la gestion par Enter car maintenant on clique sur l'option
  const handleKeyPress = (event) => {
    // Optionnel : on peut garder Enter pour valider si c'est un email valide et qu'il n'y a qu'une option
    if (event.key === 'Enter' && inputValue && isValidEmail(inputValue) && userOptions.length === 0) {
      addRecipient(inputValue);
      event.preventDefault();
    }
  };

  const handleSend = async () => {
    if (recipients.length === 0) {
      setStatus({
        success: false,
        error: true,
        message: 'Veuillez ajouter au moins un destinataire'
      });
      return;
    }

    if (!meeting) {
      setStatus({
        success: false,
        error: true,
        message: 'Aucune réunion sélectionnée'
      });
      return;
    }

    // Vérifier le contenu selon le type
    if (contentType === "summary" && !summary) {
      setStatus({
        success: false,
        error: true,
        message: 'Aucun résumé disponible pour cette réunion'
      });
      return;
    }

    if (contentType === "transcript" && !transcript) {
      setStatus({
        success: false,
        error: true,
        message: 'Aucune transcription disponible pour cette réunion'
      });
      return;
    }

    // Détecter si on est sur mobile pour donner des instructions spécifiques
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                     (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform));
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    setLoading(true);
    setStatus({ success: false, error: false, message: 'Envoi en cours...' });

    try {
      // Construire l'URL de la réunion
      const meetingUrl = `${window.location.origin}/meetings/${meeting.id}`;
      
      // Nom de l'expéditeur
      const senderName = currentUser?.displayName || 
                        currentUser?.email?.split('@')[0] || 
                        'Un utilisateur TranscriptionAI';

      // Envoyer à tous les destinataires avec gestion d'erreur individuelle
      const sendResults = [];
      let successCount = 0;
      let failureCount = 0;

      for (let i = 0; i < recipients.length; i++) {
        const recipient = recipients[i];
        
        // Mettre à jour le status pour indiquer le destinataire en cours
        setStatus({
          success: false,
          error: false,
          message: `Envoi en cours... (${i + 1}/${recipients.length}) - ${recipient.displayName}`
        });

        try {
          let result;
          if (contentType === "summary") {
            result = await emailService.sendSummaryEmail({
              to: recipient.email,
              meetingTitle: meeting.title,
              meetingDate: meeting.date,
              meetingDuration: meeting.duration,
              participants: meeting.participants,
              summary: summary.text,
              summaryStats: summaryStats || summary,
              meetingUrl,
              senderName,
              customMessage
            });
          } else {
            result = await emailService.sendTranscriptEmail({
              to: recipient.email,
              meetingTitle: meeting.title,
              meetingDate: meeting.date,
              meetingDuration: meeting.duration,
              participants: meeting.participants,
              transcript: transcript,
              meetingUrl,
              senderName,
              customMessage
            });
          }

          sendResults.push({
            recipient: recipient,
            success: true,
            result: result
          });
          successCount++;

        } catch (error) {
          addSystemNotification('❌ Erreur lors de l', 'error');
          
          // Adapter le message d'erreur pour mobile/iOS
          let errorMessage = error.message;
          if (isMobile && error.message.includes('Session Azure expirée')) {
            if (isIOS) {
              errorMessage = 'Session expirée sur iPhone. Actualisez la page (tirez vers le bas) et reconnectez-vous.';
            } else {
              errorMessage = 'Session expirée sur mobile. Actualisez la page et reconnectez-vous.';
            }
          }
          
          sendResults.push({
            recipient: recipient,
            success: false,
            error: errorMessage
          });
          failureCount++;

          // Petite pause entre les tentatives pour éviter le rate limiting
          if (i < recipients.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }

        // Petite pause entre chaque envoi pour éviter le rate limiting
        if (i < recipients.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }

      // Construire le message de résultat
      const contentLabel = contentType === "transcript" ? "transcription" : "résumé";
      let resultMessage = '';
      let isSuccess = successCount > 0;
      let isError = failureCount > 0;

      if (successCount === recipients.length) {
        // Tous les envois ont réussi
        const recipientText = successCount === 1 ? 
          `à ${recipients[0].displayName}` : 
          `à ${successCount} destinataires`;
        resultMessage = `${contentLabel.charAt(0).toUpperCase() + contentLabel.slice(1)} envoyé${contentType === "transcript" ? "e" : ""} avec succès ${recipientText}`;
        isError = false;
      } else if (successCount > 0) {
        // Succès partiel
        resultMessage = `${contentLabel.charAt(0).toUpperCase() + contentLabel.slice(1)} envoyé${contentType === "transcript" ? "e" : ""} à ${successCount}/${recipients.length} destinataires`;
        
        // Ajouter la liste des échecs
        const failedRecipients = sendResults.filter(r => !r.success).map(r => r.recipient.displayName);
        if (failedRecipients.length > 0) {
          resultMessage += `\n\nÉchecs : ${failedRecipients.join(', ')}`;
          
          // Ajouter les détails des erreurs
          const errorDetails = sendResults
            .filter(r => !r.success)
            .map(r => `• ${r.recipient.displayName}: ${r.error}`)
            .join('\n');
          
          if (errorDetails) {
            resultMessage += `\n\nDétails des erreurs :\n${errorDetails}`;
          }
        }
      } else {
        // Tous les envois ont échoué
        resultMessage = `Erreur lors de l'envoi du ${contentLabel}`;
        isSuccess = false;
        
        // Ajouter les détails des erreurs avec aide spécifique mobile
        const errorDetails = sendResults
          .filter(r => !r.success)
          .map(r => `• ${r.recipient.displayName}: ${r.error}`)
          .join('\n');
          
        if (errorDetails) {
          resultMessage += `\n\nDétails des erreurs :\n${errorDetails}`;
        }

        // Ajouter une aide spécifique pour iOS
        if (isIOS && sendResults.some(r => r.error && r.error.includes('Session'))) {
          resultMessage += `\n\n💡 Sur iPhone : Actualisez en tirant la page vers le bas, puis reconnectez-vous.`;
        } else if (isMobile && sendResults.some(r => r.error && r.error.includes('Session'))) {
          resultMessage += `\n\n💡 Sur mobile : Actualisez la page et reconnectez-vous.`;
        }
      }

      setStatus({
        success: isSuccess,
        error: isError,
        message: resultMessage,
        sendResults: sendResults // Garder les résultats détaillés pour debugging
      });

      // Log détaillé pour le debugging

      // Fermer le dialog après 3 secondes si tous les envois ont réussi
      if (successCount === recipients.length) {
        setTimeout(() => {
          onClose();
        }, 3000);
      } else {
        // Si il y a eu des échecs, garder le dialog ouvert pour que l'utilisateur voie les erreurs
        addSystemNotification("⚠️ Dialog gardé ouvert en raison d'erreurs", 'error');
      }

    } catch (error) {
      addSystemNotification("Erreur globale lors de l'opérationenvoi", 'error');
      
      // Message d'erreur adapté pour mobile
      let globalErrorMessage = `Erreur globale: ${error.message}`;
      if (isMobile && error.message.includes('Session')) {
        if (isIOS) {
          globalErrorMessage = 'Session expirée sur iPhone. Actualisez la page (tirez vers le bas) et reconnectez-vous avant de réessayer.';
        } else {
          globalErrorMessage = 'Session expirée sur mobile. Actualisez la page et reconnectez-vous avant de réessayer.';
        }
      }
      
      setStatus({
        success: false,
        error: true,
        message: globalErrorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      title={dialogTitle}
      subtitle={`Partagez ${contentType === "transcript" ? "la transcription" : "le résumé"} avec vos collaborateurs`}
      maxWidth="md"
      actions={!status.success ? (
        <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'center' }}>
          <BaseButton 
            onClick={handleClose} 
            variant="outlined"
            disabled={loading}
            sx={{ px: 4, py: 1 }}
          >
            Annuler
          </BaseButton>
          <BaseButton 
            onClick={handleSend} 
            variant="contained"
            loading={loading}
            startIcon={!loading ? <SendIcon /> : undefined}
            sx={{ px: 4, py: 1 }}
          >
            {loading ? 'Envoi...' : `Envoyer (${recipients.length})`}
          </BaseButton>
        </Box>
      ) : undefined}
      disableClose={loading}
      customBackgroundContent="#f8faff"
    >
      {status.success ? (
        <Box sx={{ 
          textAlign: 'center',
          py: 6,
          background: status.error 
            ? 'linear-gradient(145deg, rgba(255, 152, 0, 0.05) 0%, rgba(255, 193, 7, 0.1) 100%)'
            : 'linear-gradient(145deg, rgba(76, 175, 80, 0.05) 0%, rgba(139, 195, 74, 0.1) 100%)',
          borderRadius: '20px',
          border: status.error 
            ? '1px solid rgba(255, 152, 0, 0.2)'
            : '1px solid rgba(76, 175, 80, 0.2)',
          backdropFilter: 'blur(10px)'
        }}>
          <Box sx={{ 
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 80,
            height: 80,
            borderRadius: '20px',
            background: status.error 
              ? 'linear-gradient(135deg, #FF9800 0%, #FFC107 100%)'
              : 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)',
            boxShadow: status.error 
              ? '0 8px 24px rgba(255, 152, 0, 0.3)'
              : '0 8px 24px rgba(76, 175, 80, 0.3)',
            mb: 3
          }}>
            {status.error ? (
              <WarningIcon sx={{ fontSize: 40, color: 'white' }} />
            ) : (
              <EmailIcon sx={{ fontSize: 40, color: 'white' }} />
            )}
          </Box>
          
          <Typography variant="h6" gutterBottom sx={{ 
            fontWeight: 600, 
            color: status.error ? '#FF9800' : '#4CAF50', 
            mb: 2 
          }}>
            {status.error ? '⚠️ Envoi partiel' : '✅ Email envoyé avec succès !'}
          </Typography>
          
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              lineHeight: 1.6,
              whiteSpace: 'pre-line',
              maxWidth: '600px',
              mx: 'auto',
              textAlign: 'left'
            }}
          >
            {status.message}
          </Typography>

          {/* Affichage détaillé des résultats si disponible */}
          {status.sendResults && status.sendResults.length > 0 && (
            <Box sx={{ mt: 3, textAlign: 'left', maxWidth: '600px', mx: 'auto' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                📊 Détail par destinataire :
              </Typography>
              
              <Stack spacing={1}>
                {status.sendResults.map((result, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 2,
                      backgroundColor: result.success 
                        ? 'rgba(76, 175, 80, 0.08)' 
                        : 'rgba(244, 67, 54, 0.08)',
                      borderRadius: '8px',
                      border: result.success 
                        ? '1px solid rgba(76, 175, 80, 0.2)' 
                        : '1px solid rgba(244, 67, 54, 0.2)'
                    }}
                  >
                    <Box sx={{
                      minWidth: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: result.success ? '#4CAF50' : '#F44336',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {result.success ? (
                        <CheckIcon sx={{ fontSize: 16, color: 'white' }} />
                      ) : (
                        <CloseIcon sx={{ fontSize: 16, color: 'white' }} />
                      )}
                    </Box>
                    
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {result.recipient.displayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {result.recipient.email}
                      </Typography>
                      {!result.success && result.error && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                          {result.error}
                        </Typography>
                      )}
                    </Box>
                    
                    <Typography variant="caption" sx={{ 
                      color: result.success ? '#4CAF50' : '#F44336',
                      fontWeight: 500
                    }}>
                      {result.success ? 'Envoyé' : 'Échec'}
                    </Typography>
                  </Box>
                ))}
              </Stack>

              {/* Actions si il y a eu des échecs */}
              {status.error && (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    💡 Vous pouvez supprimer les destinataires qui ont reçu l'email et réessayer pour les autres
                  </Typography>
                  
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <BaseButton 
                      onClick={() => {
                        // Supprimer les destinataires qui ont reçu l'email avec succès
                        const failedRecipients = status.sendResults
                          .filter(r => !r.success)
                          .map(r => r.recipient);
                        setRecipients(failedRecipients);
                        setStatus({ success: false, error: false, message: '' });
                      }}
                      variant="outlined"
                      size="small"
                      sx={{ px: 3 }}
                    >
                      Garder seulement les échecs
                    </BaseButton>
                    
                    <BaseButton 
                      onClick={() => {
                        setStatus({ success: false, error: false, message: '' });
                      }}
                      variant="contained"
                      size="small"
                      sx={{ px: 3 }}
                    >
                      Réessayer tous
                    </BaseButton>
                  </Stack>
                </Box>
              )}
            </Box>
          )}
        </Box>
      ) : (
        <Box 
          sx={{ 
            background: 'linear-gradient(145deg, rgba(255,255,255,0.8) 0%, rgba(248,250,255,0.9) 100%)',
            borderRadius: '20px',
            p: 3,
            border: '1px solid rgba(255,255,255,0.3)',
            backdropFilter: 'blur(10px)'
          }}
        >
          {status.error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: '12px',
                '& .MuiAlert-message': {
                  fontWeight: 500
                }
              }}
            >
              {status.message}
            </Alert>
          )}

          <Stack spacing={3}>
            {/* Destinataires avec autocomplétion */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                👥 Destinataires
              </Typography>
              
                             <Autocomplete
                 freeSolo
                 options={userOptions}
                 loading={loadingUsers}
                 getOptionLabel={(option) => typeof option === 'string' ? option : option.email}
                 filterOptions={(options, params) => {
                   // Pas de filtrage côté client, on laisse notre recherche serveur faire le travail
                   return options;
                 }}
                 clearOnBlur={true}
                 blurOnSelect={true}
                                 renderOption={(props, option) => (
                   <Box component="li" {...props} sx={{ 
                     display: 'flex', 
                     alignItems: 'center', 
                     gap: 1, 
                     py: 1,
                     background: option.isExternalOption ? 'rgba(255, 152, 0, 0.05)' : 'transparent',
                     borderLeft: option.isExternalOption ? '3px solid #FF9800' : 'none',
                     pl: option.isExternalOption ? 2 : 0
                   }}>
                     {option.isExternalOption ? (
                       <EmailIcon fontSize="small" sx={{ color: '#FF9800' }} />
                     ) : (
                       <PersonAddIcon fontSize="small" color="primary" />
                     )}
                     <Box>
                       <Typography variant="body2" sx={{ fontWeight: 500 }}>
                         {option.displayName}
                         {option.isExternalOption && (
                           <Chip 
                             label="Externe" 
                             size="small" 
                             sx={{ 
                               ml: 1, 
                               height: 18, 
                               fontSize: '0.6rem',
                               background: '#FF9800',
                               color: 'white'
                             }} 
                           />
                         )}
                       </Typography>
                       <Typography variant="caption" color="text.secondary">
                         {option.isExternalOption ? 
                           "Cliquez pour ajouter cette adresse externe" :
                           `${option.email} ${option.title && option.title !== 'Adresse externe' ? `• ${option.title}` : ''}`
                         }
                       </Typography>
                     </Box>
                   </Box>
                 )}
                                 inputValue={inputValue}
                 onInputChange={(event, newInputValue) => {

                   setInputValue(newInputValue);
                 }}
                 onChange={handleAutocompleteChange}
                 value={null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Tapez un prénom, nom ou email"
                    variant="outlined"
                    size="small"
                    onKeyPress={handleKeyPress}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        background: 'rgba(255,255,255,0.8)'
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingUsers && <CircularProgress color="inherit" size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                                 disabled={loading}
                 noOptionsText={inputValue.length < 2 ? "Tapez au moins 2 caractères" : "Aucun utilisateur trouvé"}
                 selectOnFocus={false}
                 clearOnEscape={false}
                 handleHomeEndKeys={false}
              />
              
                             <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                 💡 Recherchez des utilisateurs par nom/prénom ou tapez une adresse email pour l'ajouter
               </Typography>
            </Box>

            {/* Liste des destinataires sélectionnés */}
            {recipients.length > 0 && (
              <Box sx={{ 
                background: 'rgba(255,255,255,0.6)',
                borderRadius: '16px',
                p: 2,
                border: '1px solid rgba(25, 118, 210, 0.1)',
                backdropFilter: 'blur(8px)'
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  {recipients.length} destinataire{recipients.length > 1 ? 's' : ''} sélectionné{recipients.length > 1 ? 's' : ''}
                </Typography>
                
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {recipients.map((recipient) => (
                    <Chip
                      key={recipient.id}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>{recipient.displayName}</span>
                          {recipient.isCurrentUser && (
                            <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>({recipient.email})</span>
                          )}
                        </Box>
                      }
                      onDelete={() => removeRecipient(recipient)}
                      deleteIcon={<DeleteIcon />}
                      size="small"
                      sx={{
                        borderRadius: '8px',
                        background: recipient.isCurrentUser 
                          ? 'rgba(63, 81, 181, 0.1)' 
                          : 'rgba(76, 175, 80, 0.1)',
                        borderColor: recipient.isCurrentUser 
                          ? '#3F51B5' 
                          : '#4CAF50',
                        color: recipient.isCurrentUser 
                          ? '#3F51B5' 
                          : '#4CAF50',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        '& .MuiChip-deleteIcon': {
                          color: 'inherit'
                        }
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            <Divider sx={{ my: 1 }} />

            {/* Message personnalisé */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                💬 Message personnalisé (optionnel)
              </Typography>
              
              <BaseTextField
                placeholder={`Ajoutez un message personnalisé qui accompagnera ${contentType === "transcript" ? "la transcription" : "le résumé"}...`}
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                maxRows={6}
                disabled={loading}
                size="small"
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                    lineHeight: 1.6
                  }
                }}
              />
            </Box>

            {/* Aperçu du contenu */}
            <Box sx={{ 
              background: 'rgba(255,255,255,0.6)',
              borderRadius: '16px',
              p: 3,
              border: '1px solid rgba(25, 118, 210, 0.1)',
              backdropFilter: 'blur(8px)'
            }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                📄 Contenu à envoyer
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  background: contentType === "transcript" 
                    ? 'linear-gradient(135deg, #FF7043 0%, #FF5722 100%)'
                    : 'linear-gradient(135deg, #42A5F5 0%, #2196F3 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '1.2rem'
                }}>
                  {contentType === "transcript" ? "📄" : "📝"}
                </Box>
                
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {contentType === "transcript" ? "Transcription" : "Résumé"} - {meeting?.title || 'Sans titre'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {meeting?.date && new Date(meeting.date).toLocaleDateString('fr-FR')}
                    {meeting?.duration && ` • ${meeting.duration}`}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Stack>
        </Box>
      )}
    </BaseDialog>
  );
};

export default EmailDialog; 